import styled, { css } from 'styled-components';

import { ANIM, CASINO_SETTINGS, COLORS, MOBILE } from '../../../../theme/tokens';
import { hoverWash } from '../../../../theme/mixins';
import { Text } from '../../../../components';

// Shared three-up choice grid — reused by the real grid and its skeleton.
const choiceGrid = css`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

export const Grid = styled.div`
  ${choiceGrid}
`;

export const Choice = styled.button<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px;
  text-align: left;
  cursor: pointer;
  border-radius: 16px;
  /* Standard clickable-card pattern (same as the home Acquisition-channel cards):
     a grey surface fill + the shared hoverWash highlight, no "physical" border. Only
     the SELECTED card carries the orange selection ring; unselected keeps a 1px
     transparent border so selecting never shifts the layout. The grey surfaceFaint
     reads on both the dark and the white (light) panel — surfaceSoft did not on light. */
  border: 1px solid ${(p) => (p.$selected ? CASINO_SETTINGS.cardSelectedBorder : 'transparent')};
  background: ${(p) => (p.$selected ? `${CASINO_SETTINGS.cardSelectedGlow}, ${COLORS.surfaceFaint}` : COLORS.surfaceFaint)};
  transition: border-color ${ANIM.INTERACT_MS}ms, background ${ANIM.INTERACT_MS}ms;
  ${hoverWash}

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

export const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  min-width: 44px;
  height: 44px;
  padding: 0 12px;
  border-radius: 12px;
  /* The card itself is now surfaceFaint, so the chip steps to the panel surface
     (white on light · the dark card base on dark) to stay distinct from the card. */
  background: ${COLORS.surfaceCard};
`;

export const ChoiceTitle = styled(Text)`
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.textStrong};
`;

export const ChoiceDesc = styled(Text)`
  font-size: 12.5px;
  font-weight: 300;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;

export const Check = styled.span`
  position: absolute;
  top: 14px;
  right: 14px;
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 99px;
  background: ${CASINO_SETTINGS.cardIconSelected};
`;

/* Marks the saved (baseline) card once the selection has moved off it, so the
   owner can still see which type is actually live while comparing another. Sits
   in the same top-right corner as the check, but the two never share a card:
   the check follows the live selection, this follows the saved value. Reuses the
   area's green "saved" palette (same hue as the "Website currency saved" hint). */
export const CurrentTag = styled(Text)`
  position: absolute;
  top: 13px;
  right: 13px;
  padding: 3px 9px;
  border-radius: 99px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${CASINO_SETTINGS.okFg};
  background: ${CASINO_SETTINGS.okBg};
  border: 1px solid ${CASINO_SETTINGS.okBorder};
`;

export const SaveRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;

  /* Stack on mobile keeping DOM order (hint then button) so the visual and reading
     order match — the confirmation hint reads above its Save button. */
  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SavedHint = styled(Text)`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 400;
  color: ${CASINO_SETTINGS.okFg};

  ${MOBILE} {
    justify-content: center;
  }
`;

export const SkelGrid = styled.div`
  ${choiceGrid}
`;
